import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import {
  Button,
  Descriptions,
  Drawer,
  Form,
  Input,
  Space,
  Spin,
  Switch,
  Typography,
  message,
} from 'antd';

import { ApiError } from '../../../shared/api-client/index.js';
import { useToggleAutomation } from '../hooks/useAutomations.js';
import { useScenario } from '../hooks/useScenario.js';
import { useCreateWorkflowGraphScenario, usePatchScenario } from '../hooks/useScenarioMutations.js';
import {
  buildGraphFollowUpConfig,
  GRAPH_FOLLOW_UP_DEFAULT_DESCRIPTION,
  GRAPH_FOLLOW_UP_DEFAULT_NAME,
  GRAPH_FOLLOW_UP_EMAIL_BODY,
  GRAPH_FOLLOW_UP_EMAIL_SUBJECT,
} from '../lib/graphFollowUpPreset.js';

const { Text } = Typography;

interface GraphScenarioDrawerProps {
  scenarioId: string | null;
  open: boolean;
  onClose: () => void;
}

interface GraphScenarioFormValues {
  name: string;
  description: string;
  isActive: boolean;
}

function useDrawerWidth(): number | string {
  const [width, setWidth] = useState<number | string>(560);
  useEffect(() => {
    const update = () => setWidth(window.innerWidth < 640 ? '100vw' : 560);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return width;
}

export function GraphScenarioDrawer({
  scenarioId,
  open,
  onClose,
}: GraphScenarioDrawerProps): ReactElement {
  const { t } = useTranslation();
  const width = useDrawerWidth();
  const isCreate = scenarioId === null;
  const scenarioQuery = useScenario(open ? scenarioId : null);
  const create = useCreateWorkflowGraphScenario();
  const patch = usePatchScenario();
  const toggle = useToggleAutomation();
  const [form] = Form.useForm<GraphScenarioFormValues>();
  const sessionKeyRef = useRef<string | null>(null);
  const hydratedDetailRef = useRef<string | null>(null);
  const textDirtyRef = useRef({ name: false, description: false });

  const labels = useMemo(
    () => ({
      createTitle: t('crm.graph_scenario_editor.create_title'),
      editTitle: t('crm.graph_scenario_editor.edit_title'),
      subtitle: t('crm.graph_scenario_editor.subtitle'),
      name: t('crm.scenario_editor.field.name'),
      namePlaceholder: t('crm.graph_scenario_editor.name_placeholder'),
      defaultName: t('crm.automations.preset.graph_follow_up.name'),
      defaultDescription: t('crm.automations.preset.graph_follow_up.description'),
      description: t('crm.scenario_editor.field.description'),
      active: t('crm.graph_scenario_editor.active'),
      activeHint: t('crm.graph_scenario_editor.active_hint'),
      flow: t('crm.graph_scenario_editor.flow'),
      trigger: t('crm.graph_scenario_editor.trigger'),
      wait: t('crm.graph_scenario_editor.wait'),
      waitValue: t('crm.graph_scenario_editor.wait_value'),
      analysis: t('crm.graph_scenario_editor.analysis'),
      approval: t('crm.graph_scenario_editor.approval'),
      approvalValue: t('crm.graph_scenario_editor.approval_value'),
      action: t('crm.graph_scenario_editor.action'),
      subject: t('crm.graph_scenario_editor.subject'),
      body: t('crm.graph_scenario_editor.body'),
      cancel: t('crm.scenario_editor.cancel'),
      create: t('crm.graph_scenario_editor.create'),
      save: t('crm.scenario_editor.save'),
      successCreated: t('crm.graph_scenario_editor.created'),
      successSaved: t('crm.graph_scenario_editor.saved'),
      activationFailed: t('crm.graph_scenario_editor.activation_failed'),
      loadError: t('crm.scenario_editor.load_error'),
      retry: t('crm.scenario_editor.retry'),
      invalid: t('crm.scenario_editor.validation_error'),
      error: t('crm.scenario_editor.save_error'),
      conflict: t('crm.scenario_editor.version_conflict'),
      forbidden: t('crm.scenario_editor.forbidden'),
      upgrade: t('crm.scenario_editor.upgrade_needed'),
    }),
    [t],
  );

  useEffect(() => {
    if (!open) {
      sessionKeyRef.current = null;
      hydratedDetailRef.current = null;
      return;
    }
    const sessionKey = scenarioId ?? 'create';
    const isNewSession = sessionKeyRef.current !== sessionKey;
    if (isNewSession) {
      sessionKeyRef.current = sessionKey;
      hydratedDetailRef.current = null;
      textDirtyRef.current = { name: false, description: false };
    }
    if (isCreate) {
      form.setFieldsValue({
        ...(!textDirtyRef.current.name ? { name: labels.defaultName } : {}),
        ...(!textDirtyRef.current.description ? { description: labels.defaultDescription } : {}),
        ...(isNewSession ? { isActive: false } : {}),
      });
      return;
    }
    if (scenarioQuery.data) {
      const detailKey = `${scenarioQuery.data.id}:${scenarioQuery.data.updatedAt}`;
      if (hydratedDetailRef.current === detailKey) {
        form.setFieldsValue({
          ...(!textDirtyRef.current.name ? { name: labels.defaultName } : {}),
          ...(!textDirtyRef.current.description ? { description: labels.defaultDescription } : {}),
        });
        return;
      }
      hydratedDetailRef.current = detailKey;
      const hasDefaultName = scenarioQuery.data.name === GRAPH_FOLLOW_UP_DEFAULT_NAME;
      const hasDefaultDescription =
        scenarioQuery.data.description === GRAPH_FOLLOW_UP_DEFAULT_DESCRIPTION;
      textDirtyRef.current = {
        name: !hasDefaultName,
        description: !hasDefaultDescription,
      };
      form.setFieldsValue({
        name: hasDefaultName ? labels.defaultName : scenarioQuery.data.name,
        description: hasDefaultDescription
          ? labels.defaultDescription
          : (scenarioQuery.data.description ?? ''),
        isActive: scenarioQuery.data.isActive,
      });
    }
  }, [
    form,
    isCreate,
    labels.defaultDescription,
    labels.defaultName,
    open,
    scenarioId,
    scenarioQuery.data,
  ]);

  const showError = (error: unknown): void => {
    if (error instanceof ApiError) {
      if (error.status === 409) return void message.error(labels.conflict);
      if (error.status === 403) return void message.error(labels.forbidden);
      if (error.status === 402) return void message.error(labels.upgrade);
      if (error.status === 400) return void message.error(labels.invalid);
    }
    message.error(labels.error);
  };

  const handleSubmit = async (): Promise<void> => {
    try {
      const values = await form.validateFields();
      const persistedName = textDirtyRef.current.name ? values.name : GRAPH_FOLLOW_UP_DEFAULT_NAME;
      const persistedDescription = textDirtyRef.current.description
        ? values.description
        : GRAPH_FOLLOW_UP_DEFAULT_DESCRIPTION;
      if (isCreate) {
        const created = await create.mutateAsync({
          kind: 'workflow_graph',
          name: persistedName,
          description: persistedDescription.trim() === '' ? null : persistedDescription,
          config: buildGraphFollowUpConfig(),
        });
        if (values.isActive) {
          try {
            await toggle.mutateAsync({ id: created.id, isActive: true });
          } catch (error) {
            message.warning(labels.activationFailed);
            onClose();
            showError(error);
            return;
          }
        }
        message.success(labels.successCreated);
      } else if (scenarioId !== null && scenarioQuery.data) {
        await patch.mutateAsync({
          id: scenarioId,
          payload: {
            name: persistedName,
            description: persistedDescription.trim() === '' ? null : persistedDescription,
            isActive: values.isActive,
            expectedVersion: scenarioQuery.data.version,
          },
        });
        message.success(labels.successSaved);
      }
      onClose();
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) return;
      showError(error);
    }
  };

  const busy = create.isPending || patch.isPending || toggle.isPending;
  const loading = !isCreate && scenarioQuery.isLoading;

  return (
    <Drawer
      title={isCreate ? labels.createTitle : labels.editTitle}
      width={width}
      open={open}
      onClose={onClose}
      destroyOnHidden
      aria-label={isCreate ? labels.createTitle : labels.editTitle}
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{labels.cancel}</Button>
          <Button type="primary" loading={busy} onClick={() => void handleSubmit()}>
            {isCreate ? labels.create : labels.save}
          </Button>
        </Space>
      }
    >
      {loading ? (
        <div className="flex justify-center p-8">
          <Spin />
        </div>
      ) : scenarioQuery.isError ? (
        <ErrorState
          title={labels.loadError}
          error={scenarioQuery.error}
          onRetry={() => void scenarioQuery.refetch()}
        />
      ) : (
        <Space orientation="vertical" size={20} style={{ width: '100%' }}>
          <Text type="secondary">{labels.subtitle}</Text>
          <Form
            form={form}
            layout="vertical"
            requiredMark="optional"
            onValuesChange={(changed: Partial<GraphScenarioFormValues>) => {
              if (changed.name !== undefined) textDirtyRef.current.name = true;
              if (changed.description !== undefined) textDirtyRef.current.description = true;
            }}
          >
            <Form.Item
              name="name"
              label={labels.name}
              rules={[{ required: true, whitespace: true, max: 200, message: labels.invalid }]}
            >
              <Input placeholder={labels.namePlaceholder} />
            </Form.Item>
            <Form.Item name="description" label={labels.description} rules={[{ max: 500 }]}>
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item name="isActive" label={labels.active} valuePropName="checked">
              <Switch aria-label={labels.active} />
            </Form.Item>
            <Text type="secondary" style={{ display: 'block', marginTop: -16 }}>
              {labels.activeHint}
            </Text>
          </Form>

          <div>
            <Text strong>{labels.flow}</Text>
            <Descriptions
              bordered
              size="small"
              column={1}
              style={{ marginTop: 8 }}
              items={[
                {
                  key: 'trigger',
                  label: labels.trigger,
                  children: 'deal.stage.changed → Contacted',
                },
                { key: 'wait', label: labels.wait, children: labels.waitValue },
                { key: 'analysis', label: labels.analysis, children: 'analyze_deal_risk' },
                { key: 'approval', label: labels.approval, children: labels.approvalValue },
                { key: 'action', label: labels.action, children: 'queue_deal_email' },
              ]}
            />
          </div>

          <Descriptions
            bordered
            size="small"
            column={1}
            items={[
              { key: 'subject', label: labels.subject, children: GRAPH_FOLLOW_UP_EMAIL_SUBJECT },
              {
                key: 'body',
                label: labels.body,
                children: (
                  <Text style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {GRAPH_FOLLOW_UP_EMAIL_BODY}
                  </Text>
                ),
              },
            ]}
          />
        </Space>
      )}
    </Drawer>
  );
}
import { ErrorState } from '../../../shared/ui/ErrorState.js';
